/**
 * Compiled DSP payloads -- the wire format between TypeScript and the worklets.
 *
 * The profile schema is rich, provenance-tagged and human-authored. The
 * worklets need something flat, numeric and cheap to transfer. The compilers in
 * src/dsp/compile*.ts turn the former into the latter.
 *
 * This indirection is what makes measured data drop-in: a measured frequency
 * response compiles into exactly the same FIR payload a modelled one does, so
 * upgrading a profile never touches the DSP (spec 7).
 *
 * These interfaces must stay in sync with public/worklets/*.js. The field names
 * here ARE the field names the worklets read.
 */

/** Nonlinear kind enum shared with the worklets (see 00-kernel.js). */
export const NL = {
  NONE: 0,
  POLYNOMIAL: 1,
  TANH: 2,
  ASYM_TANH: 3,
  TRIODE: 4,
  PENTODE: 5,
  CLASS_B_CROSSOVER: 6,
  CLASS_D_PWM: 7,
  TRANSFORMER_CORE: 8,
  R2R_INL: 9,
  SIGMA_DELTA: 10,
  JFET_SQUARE: 11,
  ELECTROSTATIC: 12,
  PLANAR: 13,
  DYNAMIC_DRIVER: 14,
  CUSTOM_CURVE: 15,
} as const;

export const NL_NAMES: Record<number, string> = {
  0: 'none', 1: 'polynomial', 2: 'tanh', 3: 'asym-tanh', 4: 'triode',
  5: 'pentode', 6: 'class-b crossover', 7: 'class-d pwm', 8: 'transformer core',
  9: 'r2r ladder INL', 10: 'sigma-delta', 11: 'jfet square-law', 12: 'electrostatic',
  13: 'planar', 14: 'dynamic driver', 15: 'custom curve',
};

/**
 * A single biquad section. Cascades are flattened to [b0,b1,b2,a1,a2] * n
 * Float32Arrays before transfer; this is the authoring/analysis form.
 */
export interface CompiledBiquad {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
}

/** Noise shape enum: 0 white, 1 pink, 2 thermal, 3 HF-shaped. */
export const NOISE_SHAPE = { WHITE: 0, PINK: 1, THERMAL: 2, SHAPED: 3 } as const;

export interface WNonlinear {
  kind: number;
  amount: number;
  /** Linear, not dB. Input is divided by this before shaping. */
  headroom: number;
  bias: number;
  asymmetry: number;
  coeffs: Float32Array;
  table: Float32Array;
  /** [0] crossover width, [1] core knee, [2] hysteresis, [3..] reserved. */
  extra: Float32Array;
}

export interface WHarmonics {
  /** Linear amplitude of each harmonic at refLevelLinear, index 0 = H2. */
  amps: Float32Array;
  /** dB/dB growth per harmonic, index 0 = H2. */
  growth: Float32Array;
  refLevelLinear: number;
  asymmetry: number;
}

export interface WNoise {
  levelLinear: number;
  shape: number;
  shaperOrder: number;
  channelCorrelation: number;
}

export interface WChannel {
  gainL: number;
  gainR: number;
  crosstalkLinear: number;
  crosstalkSlopeDbDec: number;
  skewSamples: number;
}

export interface CompiledDacStage {
  id: string;
  label: string;
  oversample: number;
  /** 1 = sample-and-hold reconstruction (non-oversampling design). */
  nosHold: number;
  /** Interpolation FIR at the oversampled rate, gain-compensated by `oversample`. */
  firTaps: Float32Array | null;
  /** Decimation FIR back to the context rate. */
  downTaps: Float32Array | null;
  /** Zero-order-hold droop compensation, applied at 1x before interpolation. */
  droopTaps: Float32Array | null;
  /** Analog output-stage magnitude response, as [b0,b1,b2,a1,a2] * n. */
  analogEq: Float32Array;
  nonlinear: WNonlinear;
  harmonics: WHarmonics;
  noise: WNoise;
  channel: WChannel;
  /** Effective word length for the conversion model; 0 disables quantisation. */
  effectiveBits: number;
  ladderMismatchLsb: number;
  /** 4096-entry INL table indexed by the top of the code word. */
  inlTable: Float32Array | null;
  jitterPsRms: number;
  /** 0 random, 1 periodic, 2 mixed. */
  jitterKind: number;
  jitterToneHz: number;
  /** Peak sample-to-sample change permitted at 1x; 0 = unlimited. */
  slewLimitPerSample: number;
  /**
   * Set when the profile's reconstruction filter was too long to run in real
   * time and a shorter approximation was substituted. Null means the filter is
   * running at the length the profile declares.
   */
  firCapped: { wanted: number; used: number } | null;
  outputScale: number;
}

export interface CompiledAmpStage {
  id: string;
  label: string;
  oversample: number;
  /**
   * Multiplier applied BEFORE the nonlinear section. Unity: the nonlinear
   * section is input-referred, so the amplifier's voltage gain does not drive
   * its own waveshaper. See compileAmp for why this matters.
   */
  gainLinear: number;
  postScale: number;
  /** The amplifier's real voltage gain, for display and level reporting. */
  voltageGainDb: number;
  eq: Float32Array;
  /**
   * Voltage divider formed by the amplifier's output impedance and the
   * transducer's impedance curve. Compiled from BOTH profiles.
   */
  loadEq: Float32Array;
  upTaps: Float32Array | null;
  downTaps: Float32Array | null;
  nonlinear: WNonlinear;
  harmonics: WHarmonics;
  noise: WNoise;
  channel: WChannel;
  /** 0 soft, 1 hard, 2 asymmetric. */
  clipKind: number;
  clipCeiling: number;
  clipKneeLinear: number;
  clipStickiness: number;
  sagDb: number;
  sagAttackCoef: number;
  sagReleaseCoef: number;
  thermalDb: number;
  thermalCoef: number;
  slewLimitPerSample: number;
  transformerSat: number;
  transformerHyst: number;
  transformerLfHz: number;
}

export interface CompiledOutputStage {
  id: string;
  label: string;
  /** 0 loudspeaker, 1 headphone. */
  mode: number;
  oversample: number;
  firTaps: Float32Array | null;
  firTapsR: Float32Array | null;
  eq: Float32Array;
  eqR: Float32Array;
  resonances: Float32Array;
  upTaps: Float32Array | null;
  downTaps: Float32Array | null;
  nonlinear: WNonlinear;
  harmonics: WHarmonics;
  channel: WChannel;
  excursionKneeHz: number;
  excursionRiseDbOct: number;
  compressionDb: number;
  levelScale: number;
}

export interface CompiledSpatialStage {
  /** 0 loudspeaker room, 1 headphone crossfeed. */
  mode: number;
  enabled: number;
  width: number;
  crosstalkLinear: number;
  /** [delaySamples, gainDirect, gainOpposite, lpCoef] * n */
  reflections: Float32Array;
  lateGain: number;
  lateDelaySamples: number;
  lateDecaySeconds: number;
  crossfeedAmount: number;
  crossfeedDelaySamples: number;
  crossfeedLpCoef: number;
  mixPercent: number;
}

/** Everything needed to describe one complete chain to the engine. */
export interface CompiledSystem {
  dac: CompiledDacStage;
  amp: CompiledAmpStage;
  output: CompiledOutputStage;
  spatial: CompiledSpatialStage;
  /** Linear gain of the whole chain at 1 kHz -- the level-matching reference. */
  chainGain1k: number;
  /** Total added latency in samples (FIR group delay etc.). */
  latencySamples: number;
  /** Per-stage DSP cost estimate in MAC/sample, for the raw DSP view. */
  cost: { dac: number; amp: number; output: number; spatial: number };
  /**
   * What the engine did for each cable slot, including the slots where the
   * answer is "nothing, and here is why". Produced by the compiler so the
   * interface reports the DSP's actual behaviour rather than asserting its own.
   */
  cables: Partial<Record<import('./system').CableSlotId, import('../dsp/cables').CableEffectReport>>;
  /** Anything the user should know about this chain, surfaced in the inspector. */
  notes: string[];
  /** Non-fatal validity problems, e.g. an electrostatic headphone on a normal amp. */
  warnings: string[];
}

export type Transferables = ArrayBuffer[];

/**
 * Collect the ArrayBuffers in a payload so postMessage can transfer rather than
 * clone them. Guards against transferring the same buffer twice, which throws.
 */
export function collectTransferables(obj: unknown, out: Transferables = [], depth = 0): Transferables {
  if (!obj || typeof obj !== 'object' || depth > 8) return out;
  if (ArrayBuffer.isView(obj)) {
    const buf = (obj as ArrayBufferView).buffer;
    if (buf instanceof ArrayBuffer && !out.includes(buf)) out.push(buf);
    return out;
  }
  if (Array.isArray(obj)) {
    for (const v of obj) collectTransferables(v, out, depth + 1);
    return out;
  }
  for (const v of Object.values(obj as Record<string, unknown>)) {
    collectTransferables(v, out, depth + 1);
  }
  return out;
}

/**
 * Empty-payload FACTORIES, not shared constants.
 *
 * Payloads are transferred to worklets, which detaches their ArrayBuffers. A
 * shared constant would be detached the first time it was sent and would then
 * poison every later payload that reused it, so each caller gets its own.
 */
export const emptyNonlinear = (): WNonlinear => ({
  kind: 0, amount: 0, headroom: 1, bias: 0, asymmetry: 0,
  coeffs: new Float32Array(0), table: new Float32Array(0), extra: new Float32Array(8),
});

export const emptyHarmonics = (): WHarmonics => ({
  amps: new Float32Array(0), growth: new Float32Array(0), refLevelLinear: 0.5, asymmetry: 0,
});

export const emptyNoise = (): WNoise => ({
  levelLinear: 0, shape: 0, shaperOrder: 0, channelCorrelation: 0,
});

export const unityChannel = (): WChannel => ({
  gainL: 1, gainR: 1, crosstalkLinear: 0, crosstalkSlopeDbDec: 0, skewSamples: 0,
});
